/*
  The TCG | Sports toggle (docs/specs/pop-report-batch3.md D1, D2).

  It appears twice, at the top of the header's Games menu and as the home page's CATEGORY
  control, and both read and write the same useHomeCategory choice.

  A labelled group of two plain buttons that report aria-pressed, the same pattern as the
  language toggle (LanguageToggle) and GameView's display-mode buttons. Two looks:

  - `chrome` for the Games menu, which is fixed dark in both themes (AppBar's D14 note), so
    every colour is pcg-onAccent or goldChrome, never a themed token;
  - `page` for the home page, made from themed tokens like the form controls beside it.
*/

import type { CSSProperties } from "react";

import { CONTROL_BASE, CONTROL_LABEL, FOCUS_RING } from "components/styles";
import type { HomeCategory } from "lib";

import { useHomeCategory } from "./useHomeCategory";

const OPTIONS: readonly HomeCategory[] = ["TCG", "Sports"];

const CHROME_FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-pcg-goldChrome";

const SEGMENT = "flex-1 px-3 text-sm font-semibold transition-colors";

const chromeButton = (active: boolean): string =>
  `${SEGMENT} rounded py-1.5 ${CHROME_FOCUS_RING} ${
    active
      ? "bg-pcg-goldChrome text-black"
      : "text-pcg-onAccent/80 hover:bg-[rgba(255,255,255,0.12)] hover:text-pcg-onAccent"
  }`;

/* 38px tall overall, the same as the Sort select beside it: 1px border, 2px pad, 6px + 20px + 6px. */
const pageButton = (active: boolean): string =>
  `${SEGMENT} rounded py-[6px] ${FOCUS_RING} ${
    active
      ? "bg-pcg-raised text-pcg-goldText"
      : "text-pcg-text hover:bg-pcg-raised hover:text-pcg-goldText"
  }`;

interface CategoryToggleProps {
  variant: "chrome" | "page";
  className?: string;
  /* The chrome variant's backing colour, so a sticky strip matches the panel it sits on. */
  style?: CSSProperties;
}

const CategoryToggle = ({ variant, className = "", style }: CategoryToggleProps) => {
  const [category, choose] = useHomeCategory();
  const chrome = variant === "chrome";

  const group = (
    <div
      role="group"
      aria-label="Category"
      className={
        chrome
          ? "flex gap-1 rounded-md border border-[rgba(255,255,255,0.16)] p-1"
          : `${CONTROL_BASE} flex gap-1 p-0.5`
      }
    >
      {OPTIONS.map(option => (
        <button
          key={option}
          type="button"
          aria-pressed={option === category}
          onClick={() => choose(option)}
          className={chrome ? chromeButton(option === category) : pageButton(option === category)}
        >
          {option}
        </button>
      ))}
    </div>
  );

  if (chrome) {
    return (
      <div className={className} style={style}>
        {group}
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {/* The group's aria-label already says it; this is the sighted reader's copy. */}
      <span aria-hidden="true" className={CONTROL_LABEL}>
        Category
      </span>
      {group}
    </div>
  );
};

export default CategoryToggle;
